import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { ObjectPaymentReportRow } from '@econtractor/types';
import { useTranslation } from 'react-i18next';
import { getObjectPayments, getPaymentReportDocument } from 'api/apiReports';
import { useAuth } from 'context/AuthContext';
import { useToast } from 'context/ToastContext';
import { canViewSales, hasUserAccess, isManagerUser, isSellerUser } from 'utils/helpers/access/hasUserAccess';
import type { DocumentPaymentTarget } from 'modules/objects';
import type { ReportDateFilterState } from '../../helper/reportObjectsStorage';
import { exportObjectPaymentsReport } from './exportObjectPaymentsReport';
import { createDefaultPaymentFilters, groupPayments, loadPaymentFilters, savePaymentFilters, sortPayments, type PaymentGroupBy, type PaymentReportState, type PaymentSortKey } from './reportObjectPayments';

export const useReportObjectPayments = (objectId?: number) => {
    const { t, i18n } = useTranslation(['Reports', 'common']);
    const { auth } = useAuth();
    const { showToast } = useToast();
    const [filters, setFilters] = useState(() => loadPaymentFilters(objectId !== undefined));
    const [payments, setPayments] = useState<ObjectPaymentReportRow[] | null>(null);
    const [isReporting, setIsReporting] = useState(false);
    const [error, setError] = useState(false);
    const [reload, setReload] = useState(0);
    const [sortKey, setSortKey] = useState<PaymentSortKey>('ID');
    const [direction, setDirection] = useState<'asc' | 'desc'>('desc');
    const [documentTarget, setDocumentTarget] = useState<DocumentPaymentTarget | null>(null);
    const [documentLoading, setDocumentLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const documentRequest = useRef(0);
    const toastRef = useRef(showToast);
    toastRef.current = showToast;
    const allowed = hasUserAccess(auth?.user, 'reportsView') && (isManagerUser(auth?.user) || isSellerUser(auth?.user));
    const showSales = canViewSales(auth?.user);
    const userId = auth?.user?.ID;
    const accessKey = JSON.stringify(auth?.user?.access);
    const isAdmin = auth?.user?.admin;
    const locale = i18n.language === 'ua' ? 'uk' : i18n.language;
    const { includeObjects, isVat } = filters;
    const includeSales = showSales && filters.includeSales;
    const includeServiceSales = showSales && filters.includeServiceSales;
    const fromDate = filters.dateFilter.dates?.[0];
    const toDate = filters.dateFilter.dates?.[1];

    useEffect(() => { savePaymentFilters(filters, objectId !== undefined); }, [filters, objectId]);
    useEffect(() => () => { documentRequest.current += 1; }, []);
    useEffect(() => {
        let cancelled = false;
        documentRequest.current += 1;
        setDocumentTarget(null);
        setDocumentLoading(false);
        if (!allowed || fromDate === undefined || toDate === undefined) {
            setPayments(null);
            return;
        }
        if (isVat === -1 || (!includeObjects && !includeSales && !includeServiceSales)) {
            setPayments([]);
            setIsReporting(false);
            setError(false);
            return;
        }
        setIsReporting(true);
        setError(false);
        const load = async () => {
            const result = await getObjectPayments(fromDate, toDate, { objectId, includeObjects, includeSales, includeServiceSales, isVat });
            if (cancelled) return;
            setIsReporting(false);
            if (!result) {
                setError(true);
                setPayments((previous) => previous ?? []);
                return;
            }
            setPayments(result.payments);
        };
        void load();
        return () => { cancelled = true; };
    }, [allowed, userId, accessKey, isAdmin, objectId, fromDate, toDate, includeObjects, includeSales, includeServiceSales, isVat, reload]);

    const patch = useCallback((value: Partial<PaymentReportState>) => setFilters((prev) => ({ ...prev, ...value })), []);
    const setDates: Dispatch<SetStateAction<[number, number] | null>> = (value) => setFilters((prev) => ({
        ...prev,
        dateFilter: { ...prev.dateFilter, dates: typeof value === 'function' ? value(prev.dateFilter.dates) : value },
    }));
    const changeDate = (dateFilter: ReportDateFilterState) => patch({ dateFilter });
    const changeView = (view: 'summary' | PaymentGroupBy) => patch(view === 'summary' ? { detail: false } : { detail: true, groupBy: view });
    const sort = (key: PaymentSortKey) => {
        if (filters.detail) return;
        setDirection(key === sortKey && direction === 'asc' ? 'desc' : 'asc');
        setSortKey(key);
    };
    const filteredPayments = useMemo(() => (payments || []).filter((row) =>
        (!filters.selectedObject || Number(row.objectId) === Number(filters.selectedObject.ID))
        && (!filters.owner || Number(row.ownerId) === Number(filters.owner.ID))
        && (!filters.author || Number(row.authorId) === Number(filters.author.ID))
    ), [payments, filters.selectedObject, filters.owner, filters.author]);
    const rows = useMemo(() => sortPayments(filteredPayments, sortKey, direction, locale), [filteredPayments, sortKey, direction, locale]);
    const groups = useMemo(() => groupPayments(filteredPayments, filters.groupBy, locale), [filteredPayments, filters.groupBy, locale]);
    const openDocument = async (row: ObjectPaymentReportRow) => {
        const request = ++documentRequest.current;
        setDocumentLoading(true);
        const document = await getPaymentReportDocument(row.ID);
        if (request !== documentRequest.current) return;
        setDocumentLoading(false);
        if (!document) {
            toastRef.current(t('common:messError'), 'danger');
            return;
        }
        setDocumentTarget({ generationId: document.ID, title: document.proposalName || `#${document.ID}`, initialTab: 'document', row: document });
    };
    const exportReport = async () => {
        if (exporting || isReporting || error || !rows.length) return;
        setExporting(true);
        try {
            await exportObjectPaymentsReport({ rows, groups, filters: { ...filters, includeSales, includeServiceSales }, sortKey, direction, t });
        } catch {
            toastRef.current(t('common:messError'), 'danger');
        } finally {
            setExporting(false);
        }
    };
    const refreshReport = () => setReload((previous) => previous + 1);
    const resetReport = () => {
        setFilters(createDefaultPaymentFilters());
        setSortKey('ID');
        setDirection('desc');
        refreshReport();
    };
    return {
        refreshReport, resetReport,
        objectId, allowed, showSales, filters, payments, rows, groups, isReporting, error, exporting,
        applyFilters: patch,
        sortKey, direction, sort, setDates, changeDate, changeView,
        documentTarget, setDocumentTarget, documentLoading, openDocument, exportReport,
        retry: () => setReload((prev) => prev + 1),
    };
};
